/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import type React from "react";

import { buttonVariants } from "@/components/atoms/buttons";
import ProductCard from "@/components/molecules/product-card";
import StorefrontRouteGuard from "@/components/molecules/storefront-route-guard";
import { Sidebar, SidebarContent, SidebarHeader, SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import type { Locales } from "@/lib/i18n";
import { __, _x, getPermalink } from "@/lib/i18n/functions";
import { initMeiliSearch } from "@/lib/meilisearch";
import { resolveProductConditioning } from "@/lib/product-units";
import { shouldShowEmptySearchState, shouldShowSearchResultCount } from "@/lib/search-results-state";
import { cn } from "@/lib/utils";
import { segments } from "@/locales/segment";
import { ArrowUpRight, SearchX } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  Configure,
  Hits,
  InstantSearch,
  Pagination,
  RefinementList,
  SortBy,
  useInstantSearch,
  useRefinementList,
} from "react-instantsearch";

interface SearchResultsProps {
  locale: Locales;
  query?: string;
}

const SafeConfigure = Configure as React.ComponentType<any>;

const SearchResultsListing = ({ locale, query = "" }: SearchResultsProps) => {
  const [searchClient, setSearchClient] = useState<any>(null);
  const [isClient, setIsClient] = useState(false);
  const [hasCategories, setHasCategories] = useState(false);

  const indexName = `products_${locale}`;

  useEffect(() => {
    try {
      const { searchClient: client } = initMeiliSearch();
      setSearchClient(client);
      setIsClient(true);
    } catch (error) {
      console.error("Erreur lors de l'initialisation de MeiliSearch:", error);
    }
  }, []);

  // Check if categories block is present on navbar
  useEffect(() => {
    const check = () => {
      setHasCategories(!!document.getElementById("categories-block"));
    };

    check();

    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  if (!isClient || !searchClient) {
    return (
      <div className="grid w-full grid-cols-1 gap-x-5 px-5 py-10 lg:grid-cols-[380px_1fr]">
        <div className="row-start-1 mb-6 h-6 w-full rounded bg-gray-200 lg:col-start-2"></div>
        <aside className="col-start-1 row-start-2 w-full max-w-[380px] flex-shrink-0">
          <div className="h-64 animate-pulse rounded bg-gray-200"></div>
        </aside>
        <div className="w-full lg:col-start-2 lg:row-start-2">
          <div className="animate-pulse">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              <div className="h-6 w-full rounded bg-gray-200"></div>
              <div className="h-6 w-full rounded bg-gray-200"></div>
              <div className="h-6 w-full rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const Hit = ({ hit }: { hit: any }) => {
    const lastLevel = Object?.keys(hit?.categories || {})?.sort((a, b) => {
      // extraire les numéros (lvl0 -> 0, lvl3 -> 3)
      return Number.parseInt(b.replace("lvl", "")) - Number.parseInt(a.replace("lvl", ""));
    })[0];

    const lastCategoryFull = hit.categories ? hit.categories[lastLevel] : "";

    // Extraire uniquement la dernière sous-catégorie (la plus spécifique)
    const lastCategory = Array.isArray(lastCategoryFull)
      ? lastCategoryFull?.[0]?.split(" > ").pop()
      : lastCategoryFull?.split(" > ").pop();

    return (
      <StorefrontRouteGuard locale={locale} slug={hit.slug}>
        <ProductCard
          locale={locale}
          key={hit.id}
          productId={hit.slug}
          medusaId={hit?.medusaId || hit?.id}
          medusaVariantId={hit.medusaVariantId}
          productName={hit.title}
          isOutOfStock={hit?.status ? (hit.status === "draft" ? true : false) : false}
          productPrice={hit.price}
          productImage={hit.featuredImage ? hit.featuredImage : hit.images?.[0]}
          productTag={lastCategory}
          reference={hit.sku}
          link={`${locale != "fr" ? "/" + locale : ""}/${segments[locale || "fr"].product}/${hit.slug}`}
          isCollection={hit.isCollection}
          productConditioning={resolveProductConditioning(hit.conditioning, hit.conditioning_bac)}
          variant={hit.image_full_size_card ? "image-only" : "default"}
          isRange={hit?.is_surface_based ? true : false}
          isPriceDynamic={hit?.is_price_dynamic ? true : false}
          forSale={hit?.product_type ? (hit?.product_type === "Sale Product" ? true : false) : false}
        />
      </StorefrontRouteGuard>
    );
  };

  // Custom Filter Trigger Component
  const FilterTrigger = () => {
    const { toggleSidebar } = useSidebar();
    const { results, status } = useInstantSearch();
    const showResultCount = shouldShowSearchResultCount(status, results.__isArtificial === true);

    return (
      <button
        className="flex items-center gap-1 rounded-md border border-[#003349]/12 p-2 text-[0.75rem] text-[#003349] xl:hidden"
        onClick={toggleSidebar}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="17"
          fill="none"
          viewBox="0 0 18 17"
          aria-hidden="true"
        >
          <path
            stroke="#003349"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.25"
            d="M2.922 5.667h8.5m0 0a2.125 2.125 0 1 0 4.25 0 2.125 2.125 0 0 0-4.25 0m-4.25 5.666h8.5m-8.5 0a2.125 2.125 0 1 1-4.25 0 2.125 2.125 0 0 1 4.25 0"
          />
        </svg>
        {__("Filter", locale)}
        <span className="text-dark-primary text-xs" aria-busy={!showResultCount}>
          {showResultCount ? results.nbHits : __("Loading...", locale)}
        </span>
      </button>
    );
  };

  const HeaderResults = () => {
    const { results, status } = useInstantSearch();
    const showResultCount = shouldShowSearchResultCount(status, results.__isArtificial === true);

    return (
      <div className="mt-5 mb-6 xl:col-start-2 xl:row-start-1 xl:mt-0">
        <div className="flex flex-col-reverse items-center gap-[0.875rem] md:flex-row">
          <h1 className="text-1xl font-cabinet-grotesk font-medium md:text-3xl">
            {__("Search results", locale)} {query !== "" && `"${query}"`}
          </h1>
          <span className="hidden text-sm text-[#9C9C9C] md:block" aria-busy={!showResultCount} aria-live="polite">
            {showResultCount ? `${results.nbHits} ${segments[locale || "fr"].product}` : __("Loading...", locale)}
          </span>
          <div className="ml-auto flex items-center gap-5">
            <div className="hidden items-center gap-5 text-sm xl:flex">
              <span>{__("Sort by", locale)}</span>
              <SortBy
                items={[
                  { label: __("Pertinence", locale), value: `${indexName}` },
                  { label: __("Price (ascending)", locale), value: `${indexName}:price:asc` },
                  { label: __("Price (descending)", locale), value: `${indexName}:price:desc` },
                ]}
                className="rounded-md border border-[#9C9C9C]/38 pr-3 text-sm focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[#003349] focus-within:outline-dashed [&>select]:rounded-md [&>select]:px-3 [&>select]:py-[0.625rem] [&>select]:outline-none"
              />
            </div>
            {/* Custom Filter Trigger */}
            <FilterTrigger />
          </div>
        </div>
      </div>
    );
  };

  const CategoriesSection = () => {
    const { items } = useRefinementList({
      attribute: "categories.lvl0",
    });

    return items.length > 0 ? (
      <div>
        <h3 className="py-[0.625rem] text-sm font-bold uppercase">{segments[locale || "fr"].categories}</h3>
        <RefinementList attribute="categories.lvl0" />
      </div>
    ) : null;
  };

  const CollectionsSection = () => {
    const { items } = useRefinementList({
      attribute: "collections",
    });

    return items.length > 0 ? (
      <div>
        <h3 className="py-[0.625rem] text-sm font-bold uppercase">{__("Collections", locale)}</h3>
        <RefinementList attribute="collections" />
      </div>
    ) : null;
  };

  const TagsSection = () => {
    const { items } = useRefinementList({
      attribute: "tags",
    });

    return items.length > 0 ? (
      <div>
        <h3 className="py-[0.625rem] text-sm font-bold uppercase">{__("Tags", locale)}</h3>
        <RefinementList attribute="tags" />
      </div>
    ) : null;
  };

  const ViewResultsButton = () => {
    const { results, status } = useInstantSearch();
    const { toggleSidebar } = useSidebar();
    const showResultCount = shouldShowSearchResultCount(status, results.__isArtificial === true);

    const handleViewResults = () => {
      toggleSidebar();
    };

    return (
      <div className="absolute right-0 bottom-0 left-0 border-t border-gray-200 bg-white px-4 py-3">
        <button
          onClick={handleViewResults}
          className="bg-pink-primary hover:pink-overlay h-11 w-full cursor-pointer rounded-md px-4 py-3 text-sm font-medium text-white"
          aria-busy={!showResultCount}
        >
          {showResultCount
            ? _x("View {{ results }} products", { results: results.nbHits.toString() }, locale)
            : __("Loading...", locale)}
        </button>
      </div>
    );
  };

  const ClearQuery = () => {
    const router = useRouter();
    const { results, status } = useInstantSearch();
    const showResultCount = shouldShowSearchResultCount(status, results.__isArtificial === true);

    const handleClear = () => {
      router.push(getPermalink(locale, "search"));
    };

    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-cabinet-grotesk text-xl font-semibold text-gray-900">&quot;{query}&quot;</span>
          <span className="font-medium text-gray-500" aria-busy={!showResultCount} aria-live="polite">
            {showResultCount ? `${results.nbHits} ${__("results", locale)}` : __("Loading...", locale)}
          </span>
        </div>

        <button className="cursor-pointer p-1" onClick={handleClear}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 16 16">
            <path
              stroke="#393939"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M15.084.917.917 15.083m0-14.166 14.167 14.166"
            />
          </svg>
          <span className="sr-only">{__("Clear", locale)}</span>
        </button>
      </div>
    );
  };

  const FilterSidebar = () => {
    const { toggleSidebar } = useSidebar();

    return (
      <Sidebar
        collapsible="offcanvas"
        side="left"
        className="xl:hidden"
        style={
          {
            "--sidebar-width": "320px",
            "--sidebar-width-mobile": "100vw",
          } as React.CSSProperties
        }
      >
        <SidebarHeader className="border-b p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{__("Filter", locale)}</h2>
            <button
              onClick={toggleSidebar}
              className="rounded-md p-2 hover:bg-gray-100"
              aria-label={__("Close filters", locale)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                <path
                  stroke="#393939"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M15.084.917.917 15.083m0-14.166 14.167 14.166"
                />
              </svg>
            </button>
          </div>
        </SidebarHeader>
        <SidebarContent className="relative flex h-full flex-col">
          <div className="mb-[4.32rem] flex-1 overflow-y-auto">
            <div className="space-y-[0.625rem] p-4">
              <CategoriesSection />
              <CollectionsSection />
              <TagsSection />
            </div>
          </div>

          <ViewResultsButton />
        </SidebarContent>
      </Sidebar>
    );
  };

  const EmptyResults = () => (
    <div className="bg-light-primary/35 flex min-h-[320px] flex-col items-center justify-center gap-5 rounded-sm px-4 py-10 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-white">
        <SearchX className="text-primary-light size-6" aria-hidden="true" strokeWidth={1.8} />
      </div>
      <div className="space-y-2">
        <h2 className="font-cabinet-grotesk text-2xl font-medium">{__("No products found", locale)}</h2>
        <p className="text-primary-light mx-auto max-w-md">
          {__("Try another search or explore the catalogue to find the right item.", locale)}
        </p>
      </div>
      <Link
        href={getPermalink(locale, "catalogue")}
        className={cn(buttonVariants({ variant: "rounded", size: "md" }), "font-semibold")}
      >
        {__("Explore the catalogue", locale)}
        <ArrowUpRight size={18} aria-hidden="true" />
      </Link>
    </div>
  );

  const ResultsContent = () => {
    const { results, status } = useInstantSearch();
    const isArtificial = results.__isArtificial === true;

    if (shouldShowEmptySearchState(status, isArtificial, results.nbHits)) {
      return <EmptyResults />;
    }

    if (results.nbHits === 0 && !shouldShowSearchResultCount(status, isArtificial)) {
      return null;
    }

    return (
      <div className="flex h-full flex-col">
        <Hits hitComponent={Hit} />
        <div className="mt-auto pt-6">
          <Pagination
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <SidebarProvider defaultOpen={false}>
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={true}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <SafeConfigure hitsPerPage={12} query={query} sort={[`${indexName}:display:asc`]} />

        {/* Mobile/Tablet Sidebar */}
        <FilterSidebar />

        <div className="relative container grid w-full grid-cols-1 place-items-start gap-x-5 overflow-visible! px-5 pb-10 md:py-10 xl:grid-cols-[380px_1fr]">
          {/* Desktop sidebar container */}
          <div
            className={cn(
              "w-full xl:sticky xl:overflow-y-auto",
              hasCategories ? "xl:top-[11.5rem]" : "xl:top-[7.5rem]"
            )}
          >
            <div className="space-y-4">
              <aside className="hidden xl:block xl:space-y-[0.625rem]">
                {query !== "" && <ClearQuery />}
                <CategoriesSection />
                <CollectionsSection />
                <TagsSection />
              </aside>
            </div>
          </div>

          <div className="flex h-full w-full flex-col">
            <HeaderResults />
            <div className="w-full flex-1">
              <ResultsContent />
            </div>
          </div>
        </div>
      </InstantSearch>
    </SidebarProvider>
  );
};

export default SearchResultsListing;
